import React, {createRef, useContext, useEffect, useRef, useState} from "react";
import {ProjectContext} from "../../contexts/ProjectContext";
import {CompanyType} from "../../models/company";
import {Cell, RowMeta} from "./bidConfig_model";
import {parseClipboard} from "./PasteFromExcel";
import {DEV_LINE} from "../developer/plugs";
import {DevLineTag} from "../developer/PlugCell";
import {Button} from "../ui/button";
// import {moneyFormatter} from "../../lib/formatters";
import {USDonChange, USDtoValue, USDtoValueCell} from "../fields/USDField";
import {Badge} from "../ui/badge"
import {rankBadge} from "../fields/feedback";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../ui/select";
import { EyeOff, GripVertical, MoreHorizontal, Plus, Trash2 } from "lucide-react";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../ui/dialog";
import {
    DndContext,
    DragEndEvent,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy
} from "@dnd-kit/sortable";
import {CSS} from "@dnd-kit/utilities";
// the SOV editor - every change goes to the server (server bid_sov_routes) and the grid comes back from it.
// rows and columns by index, as the grid shows them now. text is saved when the cell loses focus
export type SheetEdits = {
    cellText: (i: number, j: number, text: string) => void;
    cellInput: (i: number, j: number, input: boolean) => void;
    cellType: (i: number, j: number, type: string) => void;
    // under row `after` (null - at the top)
    addRow: (after: number | null) => void;
    deleteRow: (i: number) => void;
    // row `from` ends up at index `to`
    moveRow: (from: number, to: number) => void;
    // developer only - one way
    flagRow: (i: number) => void;
    // right of column `after` (null - the first column)
    addCol: (after: number | null) => void;
    deleteCol: (j: number) => void;
    // a block from excel, its top left on [i, j]
    paste: (i: number, j: number, rows: string[][]) => void;
}

type BidFormConfigProps = {
    data: Cell[][];
    setData: (data: Cell[][]) => void;
    readonly: boolean;
    enable_row_col: boolean;
    // extra content in a cell, next to the feedback badge - eg the developer's add plug button
    cellExtra?: (i: number, j: number, cell: Cell) => React.ReactNode;
    // read-only lines under row i - eg developer plugs. cells are keyed by column
    subRows?: (i: number) => SubRow[];
    // row level data (developer only lines), lined up with data's rows
    rowMeta?: RowMeta[];
    // the SOV editor's changes - see SheetEdits
    edits?: SheetEdits;
    // bid page (developer) - developer only input cells can be typed in. called on blur when the value changed
    onDeveloperCell?: (i: number, j: number, value: any) => void;
    // bid page (developer) - the title (first column) of a developer only line can be renamed. on blur when changed
    onDeveloperLabel?: (i: number, label: string) => void;
};

export type SubRow = {
    key: string;
    cells: Record<number, React.ReactNode>;
    // classes for the filled cells
    className?: string;
}


type DataCellProps = {
    i: number,
    j: number,
    myIndex: [number, number] | null,
    cell: Cell,
    itemsRef: any,
    isBuilderBit: boolean,
    Ncol: number,
    readonly : boolean,
    handleFocus: (e:any, i:number, j:number)=> void;
    handleKeyPress: (e:any, i:number, j:number)=> void;
    setCellType: (e:any, i:number, j:number)=> void;
    setCellParty: (e:any, i:number, j:number)=> void;
    update: (e:any, i:number, j:number)=> void;
    extra?: React.ReactNode;
    // the cell is on a developer only line
    devRow?: boolean;
    // ... and the developer can type in it (bid page)
    devEditable?: boolean;
    onBlur?: (i: number, j: number) => void;
    onPaste?: (e: React.ClipboardEvent, i: number, j: number) => void;
}


function inverseFormatter(cell: Cell, new_value:any){
    if (cell.party && cell.value){
        if(cell.type === 'txt'){
            return new_value;
        } else if (cell.type === 'num'){
            return new_value;
        } else {
            // console.log('ef, ', new_value)
            return USDonChange(new_value);
        }
    }
    return new_value;
}


function cellFormatter(cell: Cell){
    if (cell.party && cell.value){
        if(cell.type === 'txt'){
            return cell.value;
        } else if (cell.type === 'num'){
            if(Number.isFinite(cell.value)  ){
                return cell.value
            }
            return cell.value.toString().replace(/[^0-9.,]/g, "");
        } else {
            return USDtoValue(cell.value);
        }
    }
    return cell.value;
}


function autoResize(el: HTMLTextAreaElement) {
    el.style.height = 'auto';
    el.style.height = el.scrollHeight + 'px';
}

function syncRowHeight(el: HTMLTextAreaElement) {
    const tr = el.closest('tr');
    if (!tr) return;
    const textareas = tr.querySelectorAll('textarea');
    // reset all to auto first so we get true scrollHeights.
    // the heights too - a height left from an earlier measure (eg a narrower column) would never shrink
    textareas.forEach(ta => { ta.style.minHeight = ''; ta.style.height = 'auto'; });
    let maxH = 0;
    textareas.forEach(ta => { if (ta.scrollHeight > maxH) maxH = ta.scrollHeight; });
    textareas.forEach(ta => { ta.style.minHeight = maxH + 'px'; });
}

/**
 *
 * @param {props} DataCellProps
 * @returns {html} DataCell
 */
function DataCell({i, j, myIndex, cell, isBuilderBit,
                      itemsRef, Ncol, readonly, update, handleFocus, handleKeyPress,
                  setCellParty, setCellType, extra, devRow, devEditable, onBlur, onPaste}: DataCellProps) {
    const isDeveloperBit = !isBuilderBit;

    // developer only lines are violet. the cells the developer can type in show a border on hover
    const background = devRow
        ? " " + DEV_LINE.cell + (devEditable ? " hover:border-violet-400" : "")
        : (!(isBuilderBit === cell.party) ? " bg-slate-200" : "");
    const devTag = devRow && j === 0;

    return <td key={j} className=" relative">
        <textarea
            ref={(el) => {
                itemsRef.current[i * Ncol + j] = el ? {current: el} : null;
                if (el) { autoResize(el); syncRowHeight(el); }
            }}
            onFocus={(e) => handleFocus(e, i, j)}
            onBlur={() => onBlur && onBlur(i, j)}
            onPaste={(e) => onPaste && onPaste(e, i, j)}
            onKeyDown={(e) => handleKeyPress(e, i, j)}
            onChange={(e) => {
                update(e, i, j);
                autoResize(e.target);
                syncRowHeight(e.target);
            }}
            value={cellFormatter(cell)}
            disabled={readonly && !devEditable}
            rows={1}
            style={{resize: 'none', overflow: 'hidden'}}
            className={
                "w-full p-1 border rounded-sm focus:outline-none" +
                " focus:ring-2 focus:ring-blue-500" +
                (isDeveloperBit && !readonly ? " pr-6" : "") +
                // room for the eye badge
                (devTag ? " pl-7" : "") +
                background
            }
            placeholder={
                devEditable
                    ? (cell.party ? "" : "line name")
                    // developer only line - bidders never fill it in
                    : devRow
                        ? ""
                        : !(isBuilderBit === cell.party) && isDeveloperBit
                            ? ((cell.type ? "(" + cell.type + "):" : "") + "bidder input")
                            : ""}
        />
        {isDeveloperBit &&
            (<>
            {!(isBuilderBit === cell.party) && !readonly  ? //
                // no checkbox on developer only lines - the type sits in its place
                <div className={"absolute top-1.5 " + (devRow ? "right-2" : "right-7")}>
                    <Select value={cell.type ? cell.type : "$"} onValueChange={(e) => setCellType(e, i, j)}>
                        <SelectTrigger className="w-[40px] h-[15px] p-1 text-xs border rounded-md bg-slate-100">
                            <SelectValue placeholder="Type" />
                        </SelectTrigger>
                        <SelectContent className="text-xs bg-slate-100">
                            <SelectItem value="$">$</SelectItem>
                            <SelectItem value="num">Number</SelectItem>
                            <SelectItem value="txt">Text</SelectItem>
                        </SelectContent>
                    </Select>
                
                </div>: <></>
            }
                    {!readonly && !devRow ?
                        <div className="absolute right-2 top-1.5">
                            <input
                                type="checkbox"
                                checked={cell.party}
                                onChange={(e) => setCellParty(e, i, j)}
                            />
                        </div>
                        :<></>
                    }
            </>
        )}
        {devTag
            ? <div className="absolute left-1 top-1">
                <DevLineTag/>
            </div>
            : <></>}
        {(cell.feedback || extra)
            ? <div className="absolute right-1 top-1 flex items-center gap-1">
                {cell.feedback ? rankBadge(cell.feedback) : null}
                {extra}
            </div>
            : <></>}
    </td>
}


/**
 * SOV editor - the menu at the top of a column (developer only - only the editor has one)
 * */
function ColumnMenu({canDelete, onInsertLeft, onInsertRight, onDelete}: {
    // the SOV keeps at least one column
    canDelete: boolean,
    onInsertLeft: () => void,
    onInsertRight: () => void,
    onDelete: () => void
}) {
    return <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
            <button type="button" aria-label="Column actions" title="Column actions"
                    className="rounded p-0.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100">
                <MoreHorizontal className="w-3 h-3"/>
            </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
            <DropdownMenuItem onSelect={onInsertLeft}>Insert column left</DropdownMenuItem>
            <DropdownMenuItem onSelect={onInsertRight}>Insert column right</DropdownMenuItem>
            <DropdownMenuSeparator/>
            <DropdownMenuItem className="text-red-600" disabled={!canDelete} onSelect={onDelete}>
                Delete column
            </DropdownMenuItem>
        </DropdownMenuContent>
    </DropdownMenu>
}


/**
 * template editor row - dragged by the grip in its first cell, so the textareas still work.
 * the "+" beside the grip adds a line under this one.
 * the line's actions sit after the last cell, away from the grip:
 * the eye makes the line developer only (it can't be turned back yet), the bin deletes it
 * */
function SortableRow({id, devOnly, onAdd, onFlag, onDelete, children}: {
    id: string,
    devOnly: boolean,
    onAdd: () => void,
    // undefined - no toggle
    onFlag?: () => void,
    onDelete: () => void,
    children: React.ReactNode
}) {
    const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({id});
    return <tr ref={setNodeRef}
               style={{transform: CSS.Translate.toString(transform), transition, opacity: isDragging ? 0.6 : 1}}
               className="border-b border-gray-200 transition-colors ">
        <td className="align-top pt-1">
            {/* "+" against the line's first cell */}
            <div className="flex items-center justify-between">
                <span className="cursor-grab text-gray-400 hover:text-gray-700" {...attributes} {...listeners}>
                    <GripVertical className="w-3 h-3"/>
                </span>
                <button type="button" onClick={onAdd}
                        aria-label="Add a line under this one" title="Add a line under this one"
                        className="rounded p-0.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100">
                    <Plus className="w-3 h-3"/>
                </button>
            </div>
        </td>
        {children}
        <td className="align-top pt-1">
            <div className="flex items-center gap-1">
                {onFlag &&
                    <button type="button" onClick={onFlag} disabled={devOnly}
                            aria-pressed={devOnly}
                            aria-label={devOnly ? "Developer only line" : "Make this line developer only"}
                            title={devOnly
                                ? "Developer only - bidders don't see this line. It can't be shared with bidders yet"
                                : "Make this line developer only"}
                            className={"rounded p-0.5 " + (devOnly
                                ? DEV_LINE.tag
                                : "text-gray-400 hover:text-gray-700 hover:bg-gray-100")}>
                        <EyeOff className="w-3 h-3"/>
                    </button>}
                <button type="button" onClick={onDelete}
                        aria-label="Delete this line" title="Delete this line"
                        className="rounded p-0.5 text-gray-400 hover:text-red-600 hover:bg-gray-100">
                    <Trash2 className="w-3 h-3"/>
                </button>
            </div>
        </td>
    </tr>
}


function DataSheet({data, setData, enable_row_col, readonly, cellExtra, subRows,
                       rowMeta, edits, onDeveloperCell, onDeveloperLabel}: BidFormConfigProps) {
    const userProjectContext = useContext(ProjectContext);
    // cell.party = { 0(false)=Developer, 1(true)=bidder }
    const isBuilderBit = userProjectContext?.role === CompanyType.builder;
    const isDeveloperBit = userProjectContext?.role === CompanyType.developer || CompanyType.broker;

    const itemsRef = useRef<(React.RefObject<HTMLInputElement | HTMLTextAreaElement> | null)[]>([]);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [myIndex, setMyIndex] = useState<[number, number] | null>(null);
    const [isFocusedIn, setIsFocusedIn] = useState<boolean>(false);
    const [colWidths, setColWidths] = useState<number[]>(() => {
        const count = data.length > 0 && data[0] ? data[0].length : 0;
        return new Array(count).fill(150); // default px per column
    });


    const Nrow = data.length;
    const Ncol = data.length > 0 && data[0] ? data[0].length : 0;

    useEffect(() => {
        if (Ncol > 0 && colWidths.length !== Ncol) {
            setColWidths(new Array(Ncol).fill(150));
        }
    }, [Ncol]);
    // the effect above catches colWidths up a render late (eg the first render after the data loads).
    // until then use the default - otherwise the actions column lands on the first data column,
    // its textareas are measured that narrow and the rows stay that tall
    const widths = colWidths.length === Ncol ? colWidths : new Array(Ncol).fill(150);

    useEffect(() => {
        if (data.length > 0 && data[0]) {
            const arrLength = data[0].length * data.length;
            itemsRef.current = Array(arrLength)
                .fill(null)
                .map((_, i) => itemsRef.current[i] || createRef<HTMLInputElement | HTMLTextAreaElement>());
        }
    }, [data]);

    // Handle click outside the container (table + buttons) to reset myIndex
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current &&
                !containerRef.current.contains(event.target as Node)) {
                setMyIndex(null);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [containerRef]);

    const handleResizeStart = (colIndex: number, e: React.MouseEvent) => {
      e.preventDefault();
      const startX = e.clientX;
      const startWidth = colWidths[colIndex];

      const onMouseMove = (moveEvent: MouseEvent) => {
          const delta = moveEvent.clientX - startX;
          setColWidths(prev => {
              const next = [...prev];
              next[colIndex] = Math.max(50, startWidth + delta); // 50px minimum
              return next;
          });
      };

      const onMouseUp = () => {
          document.removeEventListener("mousemove", onMouseMove);
          document.removeEventListener("mouseup", onMouseUp);
      };

      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
  };


    // ---- developer only lines ----
    const devRow = (i: number) => !!(rowMeta && rowMeta[i] && rowMeta[i].developer_only);
    // bid page: the developer types in the input cells of developer only lines
    const devValueEditable = (i: number, j: number) => !!onDeveloperCell && devRow(i) && data[i][j].party;
    // ... and can rename the line (its first column, when that is a label)
    const devLabelEditable = (i: number, j: number) =>
        !!onDeveloperLabel && devRow(i) && j === 0 && !data[i][j].party;
    const devEditable = (i: number, j: number) => devValueEditable(i, j) || devLabelEditable(i, j);

    // the line waiting on the 'developer only' warning dialog
    const [flagging, setFlagging] = useState<number | null>(null);

    const flagRow = (i: number) => {
        // turning it back off isn't supported yet - developer values would show to bidders
        if (!edits || devRow(i)) return;
        edits.flagRow(i);
    };

    // value when the cell was focused - only save cells that changed
    const focusValue = useRef<any>(null);
    const onCellBlur = (i: number, j: number) => {
        if (data[i][j].value === focusValue.current) return;
        if (devValueEditable(i, j) && onDeveloperCell) {
            onDeveloperCell(i, j, data[i][j].value);
        } else if (devLabelEditable(i, j) && onDeveloperLabel) {
            onDeveloperLabel(i, String(data[i][j].value ?? ""));
        } else if (edits && !data[i][j].party) {
            // the SOV editor - a label's text
            edits.cellText(i, j, String(data[i][j].value ?? ""));
        }
    };

    // the SOV editor - a block from excel pasted into a cell goes on the server with its top left there.
    // a single value pastes into the cell like typing
    const onCellPaste = (e: React.ClipboardEvent, i: number, j: number) => {
        if (!edits) return;
        const rows = parseClipboard(e.clipboardData.getData("text"));
        if (rows.length < 2 && rows[0].length < 2) return;
        e.preventDefault();
        edits.paste(i, j, rows);
    };

    const update = (event: any, i: number, j: number) => {
        // console.log('update1', event.target.value)
        // if builder and cell is input
        // or if developer and is NOT input
        // or a developer cell on a developer only line
        if (isBuilderBit === data[i][j].party || devEditable(i, j)) {

            let new_value = inverseFormatter(data[i][j], event.target.value)
            // console.log('update2', new_value)
            const newData = [...data];
            newData[i][j].value = new_value;
            setData(newData);
        }
    };

    // ---- the SOV editor's changes - each goes to the server (edits), the grid comes back from it ----

    // a blank column right of column `after` (null - the first column) - from a column's menu
    const insertCol = (after: number | null) => {
        if (!edits) return;
        const newWidths = [...colWidths];
        newWidths.splice(after === null ? 0 : after + 1, 0, 150);
        setColWidths(newWidths);
        edits.addCol(after);
    };

    const deleteCol = (j: number) => {
        // the SOV keeps at least one column
        if (!edits || Ncol < 2) return;
        const newWidths = [...colWidths];
        newWidths.splice(j, 1);
        setColWidths(newWidths);
        edits.deleteCol(j);
    };

    const deleteRow = (rowIndex: number) => {
        if (Nrow === 0 || !edits) return;
        edits.deleteRow(rowIndex);
    };

    // row drag (SOV editor) - keyed by the row's first cell, so a row keeps its key when the grid comes back
    const rowIds = data.map((row, i) => `row-${row[0]?.id ?? i}`);
    const sensors = useSensors(
        useSensor(PointerSensor, {activationConstraint: {distance: 6}}),
        useSensor(KeyboardSensor, {coordinateGetter: sortableKeyboardCoordinates})
    );

    const onRowDragEnd = (event: DragEndEvent) => {
        const {active, over} = event;
        if (!over || active.id === over.id || !edits) return;
        const from = rowIds.indexOf(String(active.id));
        const to = rowIds.indexOf(String(over.id));
        // shown moved right away - the grid that comes back has it there too
        setData(arrayMove(data, from, to));
        edits.moveRow(from, to);
    };

    const setCellParty = (e: any, i: number, j: number) => {
        // only developer can do this ...
        if (isDeveloperBit && edits) {
            edits.cellInput(i, j, e.target.checked);
        }
    };

    const setCellType = (e: React.ChangeEvent<HTMLSelectElement>, i: number, j: number) => {
        // only developer can do this ...
        if (isDeveloperBit && edits) {
            edits.cellType(i, j, (e as unknown) as string);
        }
    };

    const back = (e: any) => {
        // let last = hist.pop()
    }

    const handleKeyPress = (event: any, i: number, j: number) => {
        // todo --- navigating back and forth within a cell is funky...
        let next;
        if (event.keyCode === 38 && i > 0) {
            // up arrow
            next = itemsRef.current[(i - 1) * Ncol + j];
        } else if (event.keyCode === 37 && j > 0) {
            // left arrow key
            next = itemsRef.current[i * Ncol + j - 1];
        } else if (event.keyCode === 39 && j < Ncol - 1) {
            // right key
            next = itemsRef.current[i * Ncol + j + 1];
        } else if (event.keyCode === 40 && i < Nrow - 1) {
            // down key
            next = itemsRef.current[(i + 1) * Ncol + j];
        } else if (event.keyCode === 13 && i < Nrow - 1) {

            // enter key

            // next = itemsRef.current[(i + 1) * Ncol + j];

        } else if (event.ctrlKey && event.key === 'd') {

            event.preventDefault()
            if (isDeveloperBit && edits) {
                edits.cellInput(i, j, !data[i][j].party);
            }
            return;
        }
        if (next && next.current) next.current.focus();
    };

    const handleFocus = (event: any, i: number, j: number) => {
        setMyIndex([i, j]);
        focusValue.current = data[i][j].value;
    };

    return (
        <div ref={containerRef}>
            <div className="relative w-full overflow-auto">
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onRowDragEnd}>
              <SortableContext items={rowIds} strategy={verticalListSortingStrategy}>
                <table className="w-full text-xs text-left table-fixed border-separate" style={{borderSpacing: '2px 2px'}}>
                    <colgroup>
                      {enable_row_col && <col style={{width: '20px'}}/>}
                      {widths.map((w, idx) => (
                          <col key={idx} style={{width: `${w}px`}} />
                      ))}
                      {/* line actions */}
                      {enable_row_col && <col style={{width: edits ? '44px' : '24px'}}/>}
                  </colgroup>

                    <thead>
                        <tr>
                          {enable_row_col && <th style={{padding: 0, height: '20px'}}/>}
                          {widths.map((_, j) => (
                              <th key={j} className="relative" style={{padding: 0, height: '20px'}}>
                                  {/* the SOV editor - the column's menu */}
                                  {edits &&
                                      <div className="absolute left-0 top-0 flex items-center" style={{height: '20px'}}>
                                          <ColumnMenu canDelete={Ncol > 1}
                                                      onInsertLeft={() => insertCol(j === 0 ? null : j - 1)}
                                                      onInsertRight={() => insertCol(j)}
                                                      onDelete={() => deleteCol(j)}/>
                                      </div>}
                                  <div
                                      onMouseDown={(e) => handleResizeStart(j, e)}
                                      className="absolute right-0 top-0 w-5 cursor-col-resize hover:bg-blue-400 z-10 flex items-center justify-center"
                                      style={{height: '20px'}}
                                  >
                                    <GripVertical className="w-3 h-3 text-gray-600 hover:text-blue-400" />

                                  </div>
                              </th>
                          ))}
                          {enable_row_col && <th style={{padding: 0, height: '20px'}}/>}
                      </tr>

                    </thead>
                    <tbody className="[&_tr:last-child]:border-0">
                    {data.map((row, i) => {
                        const cells = row.map((cell, j) => (
                            <DataCell
                                key={j}
                                i={i}
                                j={j}
                                myIndex={myIndex}
                                isBuilderBit={isBuilderBit}
                                setCellParty={setCellParty}
                                setCellType={setCellType}
                                update={update}
                                itemsRef={itemsRef}
                                cell={cell}
                                readonly={readonly}
                                handleKeyPress={handleKeyPress}
                                handleFocus={handleFocus}
                                Ncol={Ncol}
                                extra={cellExtra ? cellExtra(i, j, cell) : null}
                                devRow={devRow(i)}
                                devEditable={devEditable(i, j)}
                                onBlur={onCellBlur}
                                onPaste={onCellPaste}
                            />
                        ));
                        return <React.Fragment key={rowIds[i]}>
                            {enable_row_col
                                ? <SortableRow id={rowIds[i]} devOnly={devRow(i)}
                                               onAdd={() => edits && edits.addRow(i)}
                                               onFlag={edits ? () => setFlagging(i) : undefined}
                                               onDelete={() => deleteRow(i)}>
                                    {cells}
                                </SortableRow>
                                : <tr className="border-b border-gray-200 transition-colors ">
                                    {cells}
                                </tr>}
                            {subRows && subRows(i).map((sub) =>
                                <tr key={sub.key}>
                                    {enable_row_col && <td/>}
                                    {row.map((_, j) =>
                                        <td key={j} className={sub.cells[j] !== undefined
                                            ? "px-1 py-0.5 rounded-sm " + (sub.className ?? "bg-gray-50") : ""}>
                                            {sub.cells[j]}
                                        </td>)}
                                </tr>)}
                        </React.Fragment>
                    })}
                    </tbody>
                </table>
              </SortableContext>
              </DndContext>
            </div>
            <Dialog open={flagging !== null} onOpenChange={(open) => { if (!open) setFlagging(null); }}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Make this line Internal Only</DialogTitle>
                        <DialogDescription>
                            The line will be invisible to bidders, and any values on it will be overwritten.
                            This can't be undone yet.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <DialogClose asChild>
                            <Button type="button" variant="outline">Cancel</Button>
                        </DialogClose>
                        <Button type="button" onClick={() => {
                            if (flagging !== null) flagRow(flagging);
                            setFlagging(null);
                        }}>
                            Make Internal Only
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>

    );
}

export default DataSheet